<div class="table-wrapper">
  <table class="report-table">
    <caption class="sr-only">{{ 'table.caption' | translate }}</caption>
    <thead
      class="bg-blue-100 text-left sticky top-0 z-10 border-b border-slate-200"
    >
      <tr>
        <th
          class="px-4 py-3 text-xs text-slate-500 tracking-wider whitespace-nowrap"
        >
          {{ 'table.headers.date' | translate }}
        </th>
        <th
          class="px-4 py-3 text-xs text-slate-500 tracking-wider whitespace-nowrap"
        >
          {{ 'table.headers.policyNumber' | translate }}
        </th>
        <th
          class="px-4 py-3 text-xs text-slate-500 tracking-wider whitespace-nowrap"
        >
          {{ 'table.headers.loadId' | translate }}
        </th>
        <th
          class="px-4 py-3 text-xs text-center text-slate-500 tracking-wider whitespace-nowrap"
        >
          {{ 'table.headers.operationType' | translate }}
        </th>
        <th
          class="px-4 py-3 text-xs text-center text-slate-500 tracking-wider whitespace-nowrap"
        >
          {{ 'table.headers.status' | translate }}
        </th>
        <th
          class="px-4 py-3 text-xs text-center text-slate-500 tracking-wider whitespace-nowrap"
        >
          {{ 'table.headers.progress' | translate }}
        </th>
        <th
          class="px-4 py-3 text-xs text-center text-slate-500 tracking-wider whitespace-nowrap"
        >
          {{ 'table.actions' | translate }}
        </th>
      </tr>
    </thead>
    <tbody
      class="divide-y divide-slate-100"
      [class.has-expanded]="expandedRowId() !== null"
    >
      @if (isLoading() || isRefreshing()) {
        <tr>
          <td colspan="7" class="px-3 py-2">
            <app-loader
              [type]="LoaderType.Skeleton"
              [count]="skeletonCount()"
            />
          </td>
        </tr>
      } @else {
        @for (load of loads(); track load.loadId) {
          <tr
            class="table-row hover:bg-slate-50 transition-colors"
            [class.is-expanded]="expandedRowId() === load.loadId"
          >
            <td
              class="cell-date px-3 py-2 align-middle"
              [attr.data-label]="'table.headers.date' | translate"
            >
              <span class="date-day">{{
                load.createdAt | date: 'dd/MM/yyyy'
              }}</span>
              <span class="date-time">{{
                load.createdAt | date: 'HH:mm'
              }}</span>
            </td>
            <td
              class="cell-policy px-3 py-2 align-middle"
              [attr.data-label]="'table.headers.policyNumber' | translate"
            >
              {{ load.policyNumber }}
              <button
                type="button"
                class="copy-id-btn"
                (click)="copyField(load.policyNumber)"
                [attr.aria-label]="
                  copied() === load.policyNumber
                    ? ('table.copy.policyNumberCopied' | translate)
                    : ('table.copy.copyPolicyNumber' | translate)
                "
                [title]="
                  copied() === load.policyNumber
                    ? ('table.copy.copied' | translate)
                    : ('table.copy.copyPolicyNumber' | translate)
                "
              >
                <i
                  class="pi"
                  [class.pi-copy]="copied() !== load.policyNumber"
                  [class.pi-check]="copied() === load.policyNumber"
                ></i>
              </button>
            </td>
            <td
              class="cell-id px-3 py-2 align-middle"
              [attr.data-label]="'table.headers.loadId' | translate"
            >
              <code class="load-id-code">{{ load.loadId }}</code>
              <button
                type="button"
                class="copy-id-btn"
                (click)="copyField(load.loadId)"
                [attr.aria-label]="
                  copied() === load.loadId
                    ? ('table.copy.idCopied' | translate)
                    : ('table.copy.copyId' | translate)
                "
                [title]="
                  copied() === load.loadId
                    ? ('table.copy.copied' | translate)
                    : ('table.copy.copyId' | translate)
                "
              >
                <i
                  class="pi"
                  [class.pi-copy]="copied() !== load.loadId"
                  [class.pi-check]="copied() === load.loadId"
                ></i>
              </button>
            </td>
            <td
              class="cell-type px-3 py-2 align-middle"
              [attr.data-label]="'table.headers.operationType' | translate"
            >
              {{ load.operation }}
            </td>
            <td
              class="cell-status text-center px-3 py-2 align-middle"
              [attr.data-label]="'table.headers.status' | translate"
            >
              <div class="status-cell">
                @if (
                  (load.status === LoadStatus.FAILED ||
                    load.status === LoadStatus.VALIDATION_FAILED) &&
                  load.message
                ) {
                  <button
                    type="button"
                    class="status-chip chip-{{
                      load.status.toString().toLowerCase()
                    }} chip-expandable"
                    [class.is-open]="expandedRowId() === load.loadId"
                    (click)="toggleErrorRow(load.loadId)"
                    [title]="'table.errorDetail.toggle' | translate"
                    [attr.aria-expanded]="expandedRowId() === load.loadId"
                  >
                    {{ load.statusDescription }}
                    <i class="pi pi-chevron-down chip-chevron"></i>
                  </button>
                } @else {
                  <span
                    class="status-chip chip-{{
                      load.status.toString().toLowerCase()
                    }}"
                  >
                    {{ load.statusDescription }}
                  </span>
                }
              </div>
            </td>
            <td
              class="cell-progress px-3 py-2 align-middle"
              [attr.data-label]="'table.headers.progress' | translate"
            >
              <div class="progress-wrapper">
                <div class="progress-bar-track">
                  <div
                    class="progress-bar-fill"
                    [style.width.%]="getProgress(load)"
                  ></div>
                </div>
                <span class="progress-label">{{ getProgress(load) }}%</span>
              </div>
            </td>
            <td
              class="cell-actions px-3 py-2 align-middle"
              [attr.data-label]="'table.actions' | translate"
            >
              <button
                type="button"
                class="delete-load-btn"
                [disabled]="deletingLoadId() === load.loadId"
                (click)="deleteLoad.emit(load)"
                [title]="'button.deleteBtnTitle' | translate"
                [attr.aria-label]="'button.deleteBtnTitle' | translate"
              >
                @if (deletingLoadId() === load.loadId) {
                  <i class="pi pi-spin pi-spinner"></i>
                } @else {
                  <i class="pi pi-trash"></i>
                }
              </button>
            </td>
          </tr>
          @if (
            (load.status === LoadStatus.FAILED ||
              load.status === LoadStatus.VALIDATION_FAILED) &&
            load.message &&
            expandedRowId() === load.loadId
          ) {
            <tr class="error-detail-row">
              <td colspan="7" class="error-detail-cell">
                <div class="error-detail-panel">
                  <i class="pi pi-exclamation-circle error-detail-icon"></i>
                  <div class="error-detail-body">
                    <p class="error-detail-message">{{ load.message }}</p>
                  </div>
                </div>
              </td>
            </tr>
          }
        }
        @if (loads().length === 0) {
          <tr>
            <td colspan="7" class="px-3 py-2">
              <div class="empty-rows">
                <div class="empty-row">
                  <i class="pi pi-inbox"></i>
                  <span>{{ 'table.empty' | translate }}</span>
                </div>
              </div>
            </td>
          </tr>
        }
      }
    </tbody>
  </table>
</div>
<div
  class="table-pagination"
  [class.is-disabled]="isLoading() || isRefreshing()"
>
  <p-paginator
    [rows]="rowsPerPage()"
    [first]="currentPage() * rowsPerPage()"
    [totalRecords]="totalRecords()"
    [rowsPerPageOptions]="rowsPerPageOptions()"
    [dropdownAppendTo]="'body'"
    [showCurrentPageReport]="true"
    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
    (onPageChange)="pageChange.emit($event)"
  ></p-paginator>
</div>
